/**
 * Admin Media Library — manages all uploaded images on the site.
 * Shows what's on disk, where each image is used across the site,
 * and lets the admin upload/delete files in one place.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Upload, Trash2, ExternalLink, Copy, AlertTriangle, Image as ImageIcon, Link2, Check, Home, Star } from 'lucide-react';
import api from '../lib/api';
import HelpCallout from './HelpCallout';

function fmtBytes(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [totalBytes, setTotalBytes] = useState(0);
  const [filter, setFilter] = useState('all'); // all | in_use | unused
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [copiedUrl, setCopiedUrl] = useState('');
  const [assignFor, setAssignFor] = useState(null); // {url, filename}
  const [heroSetting, setHeroSetting] = useState(null); // {url, alt} | null
  const [heroFavs, setHeroFavs] = useState([]); // [{url, alt}] — daily rotation
  const inputRef = useRef(null);

  async function loadHero() {
    try {
      const [h, f] = await Promise.all([
        api.get('/site-settings/homepage_hero'),
        api.get('/site-settings/hero_favourites'),
      ]);
      setHeroSetting(h.data.value?.url ? { ...h.data.value, updated_at: h.data.updated_at } : null);
      setHeroFavs(f.data.value?.items || []);
    } catch { /* ignore */ }
  }
  useEffect(() => { loadHero(); }, []);

  async function setAsHomepageHero(it) {
    await api.put('/admin/site-settings/homepage_hero', { value: { url: it.url, alt: it.filename } });
    loadHero();
  }
  async function clearHomepageHero() {
    await api.put('/admin/site-settings/homepage_hero', { value: {} });
    loadHero();
  }
  async function toggleFavourite(it) {
    const exists = heroFavs.some((f) => f.url === it.url);
    const next = exists ? heroFavs.filter((f) => f.url !== it.url) : [...heroFavs, { url: it.url, alt: it.filename }];
    await api.put('/admin/site-settings/hero_favourites', { value: { items: next } });
    loadHero();
  }

  async function load() {
    const r = await api.get('/admin/media');
    setItems(r.data.items || []);
    setTotalBytes(r.data.total_bytes || 0);
  }
  useEffect(() => { load(); }, []);

  async function onUpload(files) {
    setError(''); setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        await api.post('/admin/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function onDelete(item) {
    if (item.usage?.total > 0) {
      if (!window.confirm(`This image is used in ${item.usage.total} record(s). Delete the records first or replace those images.\n\nProceeding will refuse the delete.`)) return;
    } else if (!window.confirm('Permanently delete this image from the server?')) return;
    try {
      const params = item.subdir ? `?subdir=${encodeURIComponent(item.subdir)}` : '';
      const r = await api.delete(`/admin/media/${item.filename}${params}`);
      if (r.data?.error === 'in_use') {
        alert(`Cannot delete — image is in use:\n${Object.entries(r.data.usage).filter(([k,v])=>k!=='total'&&v>0).map(([k,v])=>`• ${v} ${k}`).join('\n')}`);
      } else {
        await load();
      }
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  }

  function copyUrl(url) {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(''), 1500);
  }

  const filtered = items.filter((it) => {
    if (filter === 'in_use' && (it.usage?.total || 0) === 0) return false;
    if (filter === 'unused' && (it.usage?.total || 0) > 0) return false;
    if (search && !it.filename.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const inUseCount = items.filter((i) => (i.usage?.total || 0) > 0).length;
  const unusedCount = items.length - inUseCount;

  return (
    <div className="max-w-7xl">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display text-4xl">Media library</h1>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => { if (e.target.files?.length) onUpload(Array.from(e.target.files)); }}
          data-testid="media-upload-file"
        />
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="bg-forest text-base px-5 py-3 rounded-xl font-semibold inline-flex items-center gap-2 hover:bg-forest-deep disabled:opacity-60"
          data-testid="media-upload-btn"
        >
          <Upload size={16} /> {uploading ? 'Uploading…' : 'Upload images'}
        </button>
      </div>

      <HelpCallout
        title="Every image on your site lives here."
        description="Use this library to upload new photos, replace old ones, or audit which images are still being used. The 207 images you brought across from your old UK2 site sit in the 'migrated' subfolder. Files marked 'unused' are safe to delete to save disk space — but if an image is still referenced by any attraction, town, event or package, the system will block deletion until you replace it on those records first."
        appearsAt={[
          { label: 'All attraction hero + gallery images', where: 'usage column shows the count' },
          { label: 'All town hero images', where: '/towns/<slug> pages' },
          { label: 'Events + Packages hero images', where: 'wherever they appear on the public site' },
          { label: 'New uploads here', where: 'are immediately selectable from any "Upload image" field in the admin' },
        ]}
      />

      {error && (
        <div className="bg-heritage/10 border border-heritage rounded-xl p-4 text-heritage mb-6 flex items-center gap-2" data-testid="media-error">
          <AlertTriangle size={16}/> {error}
        </div>
      )}

      {/* Homepage hero control */}
      <div className="bg-white border border-charcoal/10 rounded-2xl p-5 mb-6" data-testid="homepage-hero-panel">
        <div className="flex items-center gap-4">
        {heroSetting?.url ? (
          <>
            <img src={heroSetting.url} alt="" className="w-24 h-16 rounded-xl object-cover border border-charcoal/10 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm flex items-center gap-2"><Home size={14} className="text-heritage"/> Homepage hero image is pinned</div>
              <div className="text-xs text-muted mt-0.5 truncate">The homepage shows this photo instead of the automatic seasonal rotation. ({heroSetting.alt})</div>
              {heroSetting.updated_at && (
                <div className="text-xs text-muted mt-0.5" data-testid="homepage-hero-expiry">
                  Auto-reverts to seasonal rotation on {new Date(new Date(heroSetting.updated_at).getTime() + 7 * 86400000).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} unless you update it.
                </div>
              )}
            </div>
            <button onClick={clearHomepageHero} data-testid="homepage-hero-clear"
              className="shrink-0 bg-admin border border-charcoal/15 px-4 py-2 rounded-full text-xs font-semibold hover:border-heritage">
              Clear — back to seasonal rotation
            </button>
          </>
        ) : (
          <>
            <div className="w-12 h-12 rounded-xl bg-stone inline-flex items-center justify-center shrink-0"><Home size={18} className="text-charcoal/50"/></div>
            <div className="flex-1">
              <div className="font-semibold text-sm">Homepage hero: automatic seasonal rotation</div>
              <div className="text-xs text-muted mt-0.5">Pick any image below and press &quot;Set as homepage hero&quot; to pin your own photo. Pinned photos auto-revert to the rotation after 7 days.</div>
            </div>
          </>
        )}
        </div>
        {/* Starred favourites — daily rotation */}
        <div className="border-t border-charcoal/10 mt-4 pt-4" data-testid="hero-favourites-strip">
          <div className="flex items-center gap-2 text-sm font-semibold"><Star size={13} className="text-mustard" fill="currentColor"/> Daily hero rotation ({heroFavs.length} starred)</div>
          <div className="text-xs text-muted mt-0.5 mb-2">Starred photos take turns as the homepage hero, one per day{heroSetting?.url ? ' — paused while a photo is pinned above' : ''}. Star images below to add them.</div>
          {heroFavs.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {heroFavs.map((f) => (
                <div key={f.url} className="relative group">
                  <img src={f.url} alt={f.alt || ''} className="w-16 h-12 rounded-lg object-cover border border-charcoal/10" />
                  <button onClick={() => toggleFavourite(f)} title="Remove from rotation" data-testid={`hero-fav-remove-${f.alt}`}
                    className="absolute -top-1.5 -right-1.5 bg-heritage text-base rounded-full w-4 h-4 text-[10px] leading-none font-bold opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-admin border border-charcoal/10 rounded-2xl p-5 mb-6 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-6 text-sm">
          <span><strong>{items.length}</strong> images</span>
          <span><strong>{fmtBytes(totalBytes)}</strong> on disk</span>
          <span className="text-forest"><strong>{inUseCount}</strong> in use</span>
          <span className="text-muted"><strong>{unusedCount}</strong> unused</span>
        </div>
        <div className="flex-1" />
        <input
          type="search"
          placeholder="Search filename…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-white border border-charcoal/15 rounded-lg px-3 py-2 text-sm w-56 focus:outline-none focus:border-forest"
          data-testid="media-search"
        />
        <div className="flex gap-1 bg-white border border-charcoal/15 rounded-lg p-1 text-xs font-semibold">
          {['all', 'in_use', 'unused'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`media-filter-${f}`}
              className={`px-3 py-1.5 rounded-md transition-colors ${filter === f ? 'bg-forest text-base' : 'text-charcoal/70 hover:text-charcoal'}`}
            >
              {f === 'all' ? 'All' : f === 'in_use' ? 'In use' : 'Unused'}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-admin border border-dashed border-charcoal/20 rounded-2xl p-12 text-center text-muted">
          <ImageIcon size={32} className="mx-auto mb-3 opacity-50"/>
          {items.length === 0 ? 'No images uploaded yet. Click "Upload images" to add some.' : 'No images match your filters.'}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-grid">
          {filtered.map((it) => {
            const usageEntries = Object.entries(it.usage || {}).filter(([k, v]) => k !== 'total' && v > 0);
            const inUse = (it.usage?.total || 0) > 0;
            return (
              <div
                key={`${it.subdir}/${it.filename}`}
                className="bg-white border border-charcoal/10 rounded-2xl overflow-hidden group"
                data-testid={`media-item-${it.filename}`}
              >
                <div className="relative aspect-[4/3] bg-stone">
                  <img src={it.url} alt={it.filename} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                  <div className={`absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${inUse ? 'bg-forest text-base' : 'bg-mustard text-charcoal'}`}>
                    {inUse ? `In use × ${it.usage.total}` : 'Unused'}
                  </div>
                  {it.source === 'venue_website' && (
                    <div className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-charcoal/85 text-base"
                      title={`Collated from ${it.source_page || 'the venue website'}`} data-testid={`media-collated-badge-${it.filename}`}>
                      From venue site
                    </div>
                  )}
                </div>
                <div className="p-3 text-xs">
                  <div className="font-mono text-charcoal/70 truncate" title={it.filename}>{it.filename}</div>
                  <div className="text-charcoal/50 mt-1">{fmtBytes(it.size)}</div>
                  {usageEntries.length > 0 && (
                    <div className="mt-2 text-muted text-[11px]">
                      {usageEntries.map(([coll, n]) => <span key={coll} className="inline-block mr-2">{n} {coll}</span>)}
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-1">
                    <button
                      onClick={() => setAssignFor(it)}
                      className="flex-1 bg-forest text-base rounded-md px-2 py-1.5 text-[11px] font-semibold inline-flex items-center justify-center gap-1 hover:bg-forest-deep"
                      data-testid={`media-useon-${it.filename}`}
                    >
                      <Link2 size={11}/> Use on…
                    </button>
                    <button
                      onClick={() => copyUrl(it.url)}
                      className="bg-admin border border-charcoal/10 rounded-md p-1.5 hover:border-forest"
                      title={copiedUrl === it.url ? 'Copied!' : 'Copy URL'}
                      data-testid={`media-copy-${it.filename}`}
                    >
                      {copiedUrl === it.url ? <Check size={11}/> : <Copy size={11}/>}
                    </button>
                    <a
                      href={it.url}
                      target="_blank"
                      rel="noreferrer"
                      className="bg-admin border border-charcoal/10 rounded-md p-1.5 hover:border-forest"
                      title="Open"
                      data-testid={`media-open-${it.filename}`}
                    >
                      <ExternalLink size={11}/>
                    </a>
                    <button
                      onClick={() => onDelete(it)}
                      disabled={inUse}
                      title={inUse ? 'In use — replace it on those records first' : 'Delete'}
                      className="bg-admin border border-charcoal/10 rounded-md p-1.5 hover:border-heritage disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`media-delete-${it.filename}`}
                    >
                      <Trash2 size={11}/>
                    </button>
                  </div>
                  <div className="mt-1.5 flex gap-1.5">
                    <button
                      onClick={() => setAsHomepageHero(it)}
                      disabled={heroSetting?.url === it.url}
                      data-testid={`media-sethero-${it.filename}`}
                      className={`flex-1 rounded-md px-2 py-1.5 text-[11px] font-semibold inline-flex items-center justify-center gap-1 border ${heroSetting?.url === it.url ? 'bg-heritage text-base border-heritage cursor-default' : 'bg-admin border-charcoal/10 hover:border-heritage text-charcoal/80'}`}
                    >
                      <Home size={11}/> {heroSetting?.url === it.url ? 'Current homepage hero' : 'Set as homepage hero'}
                    </button>
                    <button
                      onClick={() => toggleFavourite(it)}
                      title={heroFavs.some((f) => f.url === it.url) ? 'Remove from daily hero rotation' : 'Star for daily hero rotation'}
                      data-testid={`media-star-${it.filename}`}
                      className={`rounded-md px-2 py-1.5 border ${heroFavs.some((f) => f.url === it.url) ? 'bg-mustard/20 border-mustard text-charcoal' : 'bg-admin border-charcoal/10 hover:border-mustard text-charcoal/60'}`}
                    >
                      <Star size={11} fill={heroFavs.some((f) => f.url === it.url) ? 'currentColor' : 'none'}/>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {assignFor && <AssignModal item={assignFor} onClose={() => setAssignFor(null)} onApplied={load} />}
    </div>
  );
}

function AssignModal({ item, onClose, onApplied }) {
  const [groups, setGroups] = useState([]);
  const [collection, setCollection] = useState('attractions');
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState('hero');
  const [applying, setApplying] = useState(false);
  const [appliedTo, setAppliedTo] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/admin/media/targets').then((r) => setGroups(r.data.groups || []));
  }, []);

  const activeGroup = groups.find((g) => g.collection === collection);
  const supportsGallery = activeGroup?.supports_gallery;
  useEffect(() => {
    // If user switches to a collection that doesn't support gallery, snap back to hero
    if (!supportsGallery && mode !== 'hero') setMode('hero');
  }, [supportsGallery, mode]);

  const filtered = (activeGroup?.items || []).filter((it) =>
    !search || (it.label || '').toLowerCase().includes(search.toLowerCase())
  );

  async function apply(target) {
    setError(''); setApplying(true);
    try {
      await api.post('/admin/media/assign', {
        url: item.url,
        collection,
        slug: target.slug,
        target_id: target.id,
        mode,
      });
      setAppliedTo({ slug: target.slug, label: target.label, mode });
      onApplied?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setApplying(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="media-assign-modal">
      <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 md:p-6 border-b border-charcoal/10 flex items-start gap-4">
          <img src={item.url} alt="" className="w-20 h-20 rounded-xl object-cover border border-charcoal/10 shrink-0" />
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-2xl mb-1">Use this image on…</h2>
            <p className="text-muted text-sm font-mono truncate">{item.filename}</p>
          </div>
          <button onClick={onClose} className="text-charcoal/40 hover:text-charcoal text-2xl leading-none" data-testid="media-assign-close">×</button>
        </div>

        {appliedTo ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 bg-forest text-base rounded-full inline-flex items-center justify-center mb-4"><Check size={28}/></div>
            <h3 className="font-display text-2xl mb-2">Done!</h3>
            <p className="text-muted mb-6">
              Image {appliedTo.mode === 'hero' ? 'set as hero of' : appliedTo.mode === 'gallery_add' ? 'added to the gallery of' : 'removed from'} <strong>{appliedTo.label}</strong>.
            </p>
            <div className="flex justify-center gap-3">
              <button onClick={() => { setAppliedTo(null); }} className="bg-admin border border-charcoal/15 px-5 py-2.5 rounded-full font-semibold text-sm hover:border-charcoal" data-testid="media-assign-another">Use on another</button>
              <button onClick={onClose} className="bg-charcoal text-base px-5 py-2.5 rounded-full font-semibold text-sm hover:bg-charcoal/90" data-testid="media-assign-done">Close</button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-5 md:p-6 border-b border-charcoal/10 flex flex-wrap items-center gap-3">
              <div className="flex gap-1 bg-admin rounded-lg p-1 text-xs font-semibold">
                {groups.map((g) => (
                  <button
                    key={g.collection}
                    onClick={() => setCollection(g.collection)}
                    data-testid={`media-assign-coll-${g.collection}`}
                    className={`px-3 py-1.5 rounded-md transition-colors ${collection === g.collection ? 'bg-forest text-base' : 'text-charcoal/70 hover:text-charcoal'}`}
                  >
                    {g.collection.charAt(0).toUpperCase() + g.collection.slice(1)} ({g.items.length})
                  </button>
                ))}
              </div>
              <input
                type="search"
                placeholder={`Search ${collection}…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 min-w-[160px] bg-admin border border-charcoal/15 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-forest"
                data-testid="media-assign-search"
              />
              <div className="flex gap-1 bg-admin rounded-lg p-1 text-xs font-semibold">
                <button
                  onClick={() => setMode('hero')}
                  data-testid="media-assign-mode-hero"
                  className={`px-3 py-1.5 rounded-md ${mode === 'hero' ? 'bg-heritage text-base' : 'text-charcoal/70'}`}
                >
                  Set as hero
                </button>
                {supportsGallery && (
                  <button
                    onClick={() => setMode('gallery_add')}
                    data-testid="media-assign-mode-gallery"
                    className={`px-3 py-1.5 rounded-md ${mode === 'gallery_add' ? 'bg-heritage text-base' : 'text-charcoal/70'}`}
                  >
                    Add to gallery
                  </button>
                )}
              </div>
            </div>

            {error && <div className="px-6 py-3 bg-heritage/10 text-heritage text-sm flex items-center gap-2"><AlertTriangle size={14}/>{error}</div>}

            <div className="overflow-y-auto p-3" data-testid="media-assign-list">
              {filtered.length === 0 ? (
                <div className="p-8 text-center text-muted text-sm">No matches in {collection}.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {filtered.map((t) => {
                    const isCurrent = t.hero_image === item.url;
                    return (
                      <button
                        key={t.id || t.slug}
                        disabled={applying || isCurrent}
                        onClick={() => apply(t)}
                        data-testid={`media-assign-target-${t.slug || t.id}`}
                        className="flex items-center gap-3 p-3 bg-admin border border-charcoal/10 rounded-xl hover:border-forest text-left disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <img src={t.hero_image} alt="" className="w-14 h-14 rounded-lg object-cover bg-stone shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate">{t.label}</div>
                          <div className="text-muted text-xs truncate">{t.slug}</div>
                        </div>
                        {isCurrent && mode === 'hero' && <span className="bg-forest text-base text-[10px] font-bold uppercase px-2 py-1 rounded-full whitespace-nowrap">Current hero</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
